import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/admin/api";
import { Logo } from "@/components/comiq/Logo";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { SurveyHeader } from "@/components/comiq/SurveyHeader";
import { SectionHeader, BlockPrompt } from "@/components/comiq/SectionHeader";
import { AnswerInput } from "@/components/comiq/AnswerInput";
import { COLORS } from "@/comiq/tokens";
import { ArrowLeft, ArrowRight, Ban, Eye, FileWarning, PencilLine } from "lucide-react";

const READ = "mx-auto max-w-[680px] px-5";

// "Buka sebagai Responden" — simulasi penuh alur responden (intro → consent →
// pertanyaan → tinjau) untuk SATU versi instrumen (draft atau published), tanpa
// token undangan. 100% read-only: satu GET, tanpa POST; tidak pernah membuat
// invitation/participant/session/answer/audit. Memakai komponen yang sama dengan
// Draft Preview dan alur responden asli (SectionHeader, BlockPrompt, AnswerInput,
// SurveyHeader) agar header dan isi selalu identik.
const PreviewBanner = () => (
  <div
    data-testid="rp-banner"
    className="sticky top-0 z-40 border-b px-4 py-2 text-center text-xs font-semibold"
    style={{ background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A" }}
  >
    <Eye size={12} className="-mt-0.5 mr-1 inline" /> Mode Preview — jawaban tidak disimpan
  </div>
);

export default function RespondentPreview() {
  const { versionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [phase, setPhase] = useState("intro"); // intro|consent|question|review
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    setError(false);
    setPhase("intro");
    setIdx(0);
    setAnswers({});
    api.get(`/admin/versions/${versionId}/respondent-view`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [versionId]);

  if (error) {
    return (
      <div className="min-h-screen" style={{ background: COLORS.paper }}>
        <div className={`${READ} py-14`} data-testid="rp-error">
          <p className="text-sm" style={{ color: COLORS.textSoft }}>Versi instrumen tidak dapat dimuat.</p>
          <Link to="/admin/instruments" className="mt-4 inline-flex items-center gap-1.5 text-sm" style={{ color: COLORS.orangeInk }} data-testid="rp-error-back-btn"><ArrowLeft size={14} /> Kembali ke instrumen</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-sm" style={{ color: COLORS.textSoft }} data-testid="rp-loading">Memuat pratinjau…</div>;

  const items = data.items || [];
  const versionBadge = (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
      style={data.version.status === "draft" ? { background: "#FEF3C7", color: "#B45309" } : { background: "#DCFCE7", color: "#15803D" }}
      data-testid="rp-version-badge"
    >
      v{data.version.version_no} · {data.version.status === "draft" ? "Draf" : data.version.status}
    </span>
  );

  if (phase === "intro") {
    return (
      <div className="min-h-screen" style={{ background: COLORS.paper }} data-testid="rp-intro">
        <PreviewBanner />
        <header className="border-b" style={{ borderColor: COLORS.line, background: COLORS.surfaceAlt }}>
          <div className="mx-auto flex max-w-[680px] items-center justify-between px-5 py-4">
            <Logo />
            {versionBadge}
          </div>
        </header>
        <main className={`${READ} py-12`}>
          <Link to={`/admin/instruments/${versionId}`} className="inline-flex items-center gap-1.5 text-sm" style={{ color: COLORS.textSoft }} data-testid="rp-back-to-editor-btn">
            <ArrowLeft size={14} /> Kembali ke editor
          </Link>
          <div className="mt-6"><Eyebrow data-testid="rp-study-label">{data.study_title}</Eyebrow></div>
          <h1 className="font-serif-editorial mt-3 text-4xl" style={{ color: COLORS.ink, letterSpacing: "-0.02em", lineHeight: 1.05 }} data-testid="rp-instrument-title">
            {data.instrument_title}
          </h1>
          <p className="mt-5 max-w-[620px] text-base" style={{ color: COLORS.textSoft, lineHeight: 1.7 }} data-testid="rp-intro-note">
            Ini simulasi tampilan yang akan dilihat responden — tanpa undangan dan tanpa menyimpan data apa pun.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="rp-facts">
            {[{ t: `${items.length} pertanyaan`, s: "Sesuai versi ini" }, { t: data.version.status === "draft" ? "Draf" : "Terbit", s: `Versi v${data.version.version_no}` }, { t: "Mode preview", s: "Tidak menyimpan data" }].map((f, i) => (
              <div key={f.s} className="rounded-[20px] border p-5" style={{ borderColor: COLORS.line, background: COLORS.surface }} data-testid={`rp-fact-${i + 1}`}>
                <div className="break-words text-sm font-semibold" style={{ color: COLORS.ink }}>{f.t}</div>
                <div className="mt-1 text-xs leading-5" style={{ color: COLORS.textSoft }}>{f.s}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setPhase("consent")} className="btn-comiq btn-accent mt-9 text-base" data-testid="rp-start-btn">
            Mulai pratinjau <ArrowRight size={17} />
          </button>
        </main>
      </div>
    );
  }

  if (phase === "consent") {
    return (
      <div className="min-h-screen" style={{ background: COLORS.paper }}>
        <PreviewBanner />
        <div className={`${READ} py-12`} data-testid="rp-consent-screen">
          <Eyebrow>Persetujuan</Eyebrow>
          {data.consent ? (
            <>
              <h1 className="font-serif-editorial mt-3 text-3xl sm:text-4xl" style={{ color: COLORS.ink }} data-testid="rp-consent-title">{data.consent.title}</h1>
              <div className="mt-5 whitespace-pre-line rounded-comiq border bg-comiq-surface-alt p-5 text-sm" style={{ borderColor: COLORS.line, color: COLORS.textSoft, lineHeight: 1.7 }} data-testid="rp-consent-body">{data.consent.body}</div>
              <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm" style={{ color: COLORS.ink }}>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="rp-consent-checkbox" />
                <span>Saya memahami dan menyetujui untuk berpartisipasi.</span>
              </label>
              <button onClick={() => { setIdx(0); setPhase("question"); }} disabled={!agree} className="btn-comiq btn-accent mt-6 disabled:opacity-40" data-testid="rp-consent-accept-btn">
                Setuju &amp; lanjutkan <ArrowRight size={16} />
              </button>
            </>
          ) : (
            <div className="mt-5 flex items-start gap-3 rounded-comiq border p-5" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }} data-testid="rp-consent-missing">
              <FileWarning size={20} className="mt-0.5 shrink-0" style={{ color: "#B45309" }} />
              <div className="text-sm" style={{ color: COLORS.textSoft }}>
                <p className="font-semibold" style={{ color: COLORS.ink }}>Persetujuan (consent) belum dikonfigurasi.</p>
                <p className="mt-1" style={{ lineHeight: 1.6 }}>Belum ada consent aktif untuk studi ini. Pada alur responden asli, survei tidak dapat dimulai sebelum consent aktif tersedia. Anda tetap dapat melihat pertanyaan pada mode preview ini.</p>
                <button onClick={() => { setIdx(0); setPhase("question"); }} className="btn-comiq btn-accent mt-4" data-testid="rp-consent-skip-btn">
                  Lewati &amp; lihat pertanyaan <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === "question") {
    const it = items[idx];
    const val = answers[it.item_id];
    const prev = idx > 0 ? items[idx - 1] : null;
    const showSection = !prev || prev.section_id !== it.section_id;
    const showPrompt = Boolean((it.group_prompt || "").trim()) && (!prev || (prev.group_prompt || "").trim() !== (it.group_prompt || "").trim());
    return (
      <div className="min-h-screen" style={{ background: COLORS.paper }}>
        <SurveyHeader current={idx + 1} total={items.length} saved={true} preview />
        <main className="relative overflow-hidden">
          <div className={`${READ} py-10`} data-testid="rp-question-screen">
            {showSection && (
              <SectionHeader
                label={it.section_label}
                title={it.section_title}
                description={it.section_description}
                instruction={it.section_instruction}
                sectionCode={it.section_code}
              />
            )}
            {showPrompt && <BlockPrompt prompt={it.group_prompt} />}
            <h2 className="mt-3 text-2xl font-semibold" style={{ color: COLORS.ink, lineHeight: 1.4 }} data-testid="rp-question-wording">{it.wording}</h2>
            {it.display_label && (
              <div className="mt-2" data-testid="rp-display-label">
                <span className="rounded-full px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide" style={{ background: "#F1EDE4", color: COLORS.textSoft }}>{it.display_label}</span>
              </div>
            )}
            <div className="mt-7">
              <AnswerInput item={it} value={val} onAnswer={(v) => setAnswers((a) => ({ ...a, [it.item_id]: v }))} testPrefix="rp-" />
            </div>
            <div className="mt-9 flex items-center justify-between">
              <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} className="btn-comiq btn-line disabled:opacity-40" data-testid="rp-prev-btn"><ArrowLeft size={16} /> Kembali</button>
              {idx < items.length - 1 ? (
                <button onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))} className="btn-comiq btn-accent" data-testid="rp-next-btn">Lanjut <ArrowRight size={16} /></button>
              ) : (
                <button onClick={() => setPhase("review")} className="btn-comiq btn-accent" data-testid="rp-to-review-btn">Tinjau jawaban <ArrowRight size={16} /></button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // review
  const empty = items.filter((it) => answers[it.item_id] === undefined).length;
  const ansLabel = (v) => v === undefined ? { t: "Belum diisi", c: "#B45309", bg: "#FEF3C7" } : v === "unsure" ? { t: "Tidak yakin", c: "#57534E", bg: "#EEEAE0" } : { t: `Jawaban: ${v}`, c: "#15803D", bg: "#DCFCE7" };
  return (
    <div className="min-h-screen" style={{ background: COLORS.paper }}>
      <PreviewBanner />
      <div className={`${READ} py-12`} data-testid="rp-review-screen">
        <Eyebrow>Tinjau</Eyebrow>
        <h1 className="font-serif-editorial mt-3 text-3xl sm:text-4xl" style={{ color: COLORS.ink }}>Tinjau sebelum mengirim</h1>
        <p className="mt-3 text-sm" style={{ color: COLORS.textSoft }} data-testid="rp-review-summary">
          {empty === 0 ? "Semua pertanyaan telah diisi." : `${empty} pertanyaan belum diisi.`}
        </p>
        <div className="mt-6 space-y-2">
          {items.map((it, i) => {
            const s = ansLabel(answers[it.item_id]);
            return (
              <div key={it.item_id} className="flex items-center gap-3 rounded-comiq border bg-comiq-surface-alt px-4 py-3" style={{ borderColor: COLORS.line }} data-testid={`rp-review-${it.stable_item_code}`}>
                <span className="font-mono text-xs font-bold" style={{ color: COLORS.ink }}>{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-sm" style={{ color: COLORS.ink }}>{it.wording}</span>
                <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: s.bg, color: s.c }}>{s.t}</span>
                <button onClick={() => { setIdx(i); setPhase("question"); }} className="rounded-full border p-1.5" style={{ borderColor: COLORS.line }} data-testid={`rp-edit-${it.stable_item_code}`} title="Ubah"><PencilLine size={13} style={{ color: COLORS.ink }} /></button>
              </div>
            );
          })}
        </div>
        <div className="mt-8 flex items-center justify-between">
          <button onClick={() => { setIdx(items.length - 1); setPhase("question"); }} className="btn-comiq btn-line" data-testid="rp-back-to-questions-btn"><ArrowLeft size={16} /> Kembali</button>
          <button disabled className="btn-comiq btn-accent cursor-not-allowed opacity-40" title="Mode preview — jawaban tidak dapat dikirim dan tidak disimpan" data-testid="rp-submit-disabled">
            <Ban size={15} /> Kirim jawaban (nonaktif)
          </button>
        </div>
        <p className="mt-4 text-xs" style={{ color: COLORS.textSoft }} data-testid="rp-submit-note">
          Mode preview — jawaban tidak dapat dikirim dan tidak disimpan.
        </p>
      </div>
    </div>
  );
}
